// src/pages/admin/manager/evaluaciones/EvaluacionesPendientesPage.tsx
import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Chip,
  Box,
  Typography,
  Button,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useNavigate } from "react-router-dom";
import {
  getEvaluacionesPendientes,
  type EvaluacionPendiente,
} from "../../../../api/formularios";

export default function EvaluacionesPendientesPage() {
  const { t } = useTranslation<"translation">();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const [pendientes, setPendientes] = useState<EvaluacionPendiente[]>([]);
  const [loading, setLoading] = useState(false);

  const loadPendientes = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getEvaluacionesPendientes();
      setPendientes(data || []);
    } catch (error: any) {
      enqueueSnackbar("Error al cargar evaluaciones pendientes", { variant: "error" });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadPendientes();
  }, [loadPendientes]);

  const getSemaforoColor = (semaforo: 'verde' | 'amarillo' | 'rojo') => {
    switch (semaforo) {
      case 'verde':
        return 'success';
      case 'amarillo':
        return 'warning';
      case 'rojo':
        return 'error';
      default:
        return 'default';
    }
  };

  const getSemaforoLabel = (semaforo: 'verde' | 'amarillo' | 'rojo') => {
    switch (semaforo) {
      case 'verde':
        return 'Bajo';
      case 'amarillo':
        return 'Medio';
      case 'rojo':
        return 'Alto';
      default:
        return '';
    }
  };

  return (
    <Stack spacing={2}>
      <Stack direction="row" alignItems="center" spacing={2}>
        <IconButton onClick={() => navigate('/admin/manager/evaluaciones')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Evaluaciones Pendientes</Typography>
      </Stack>

      <Paper>
        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Evaluación</TableCell>
                <TableCell>Asignado</TableCell>
                <TableCell>Días Pendiente</TableCell>
                <TableCell>Urgencia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pendientes.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    No hay evaluaciones pendientes
                  </TableCell>
                </TableRow>
              ) : (
                pendientes.map((pendiente, index) => (
                  <TableRow key={index}>
                    <TableCell>{pendiente.usuarioNombre}</TableCell>
                    <TableCell>{pendiente.usuarioEmail}</TableCell>
                    <TableCell>{pendiente.formularioTitulo}</TableCell>
                    <TableCell>
                      {new Date(pendiente.asignadoAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>{pendiente.diasPendiente}</TableCell>
                    <TableCell>
                      <Chip
                        label={getSemaforoLabel(pendiente.semaforo)}
                        size="small"
                        color={getSemaforoColor(pendiente.semaforo) as any}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
    </Stack>
  );
}

